import type { PriceZone } from '@greenbow/composables'

// Query parameter contract — kept short for compact shareable URLs.
//
//   zone     DK1 | DK2          Price zone
//   rate     1–22               Charge rate in kW
//   kwh      1–100              Session kWh need
//   km       1000–100000        Annual kilometres
//   deadline HH:MM              Charging deadline (24h)
//   annual   1 | 0              Annual savings reveal expanded
//   chart    1 | 0              Chart accordion expanded

export interface UrlState {
  zone?: PriceZone
  rate?: number
  kwh?: number
  km?: number
  deadline?: string
  annual?: boolean
  chart?: boolean
}

export interface UrlStateInput {
  zone: PriceZone
  rate: number
  kwh: number
  km: number
  deadline: string
  annual: boolean
  chart: boolean
}

const DEADLINE_PATTERN = /^([01]?\d|2[0-3]):[0-5]\d$/

function parseNumber(raw: string | null, min: number, max: number): number | undefined {
  if (raw === null) return undefined
  const value = Number(raw)
  if (!Number.isFinite(value) || value < min || value > max) return undefined
  return value
}

function parseBool(raw: string | null): boolean | undefined {
  if (raw === '1' || raw === 'true') return true
  if (raw === '0' || raw === 'false') return false
  return undefined
}

export function readUrlStateFromSearch(search: string): UrlState {
  const params = new URLSearchParams(search)
  const state: UrlState = {}

  const zone = params.get('zone')
  if (zone === 'DK1' || zone === 'DK2') state.zone = zone

  const rate = parseNumber(params.get('rate'), 1, 22)
  if (rate !== undefined) state.rate = rate

  const kwh = parseNumber(params.get('kwh'), 1, 100)
  if (kwh !== undefined) state.kwh = kwh

  const km = parseNumber(params.get('km'), 1000, 100000)
  if (km !== undefined) state.km = km

  const deadline = params.get('deadline')
  if (deadline && DEADLINE_PATTERN.test(deadline)) state.deadline = deadline

  const annual = parseBool(params.get('annual'))
  if (annual !== undefined) state.annual = annual

  const chart = parseBool(params.get('chart'))
  if (chart !== undefined) state.chart = chart

  return state
}

export function readUrlState(): UrlState {
  if (typeof window === 'undefined') return {}
  return readUrlStateFromSearch(window.location.search)
}

export function buildShareUrl(base: string, state: UrlStateInput): string {
  const url = new URL(base)
  url.searchParams.set('zone', state.zone)
  url.searchParams.set('rate', String(state.rate))
  url.searchParams.set('kwh', String(state.kwh))
  url.searchParams.set('km', String(state.km))
  url.searchParams.set('deadline', state.deadline)
  url.searchParams.set('annual', state.annual ? '1' : '0')
  url.searchParams.set('chart', state.chart ? '1' : '0')
  return url.toString()
}
